import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { faEllipsisH as farEllipsisH } from "@fortawesome/pro-regular-svg-icons/faEllipsisH";
import { faUndo as farUndo } from "@fortawesome/pro-regular-svg-icons/faUndo";
import { faEyeDropper } from "@fortawesome/pro-solid-svg-icons/faEyeDropper";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Divider, IconButton, Popover, styled, Tooltip, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { CustomPicker } from "react-color";
import { Alpha, EditableInput, Hue, Saturation } from "react-color/lib/components/common";
import { toState } from "react-color/lib/helpers/color";
import useEyeDropper from "use-eye-dropper";
import { createDraggableTransition } from "../../Utils";
import { ColorButton } from "../ColorButton";
import { hexToRGBColor, isSameColor } from "./utils";
const TRANSPARENT = toState("transparent");
const titleSxProps = {
    mt: 0,
    pt: "10px",
    mb: "18px"
};
const secondarySxProps = {
    color: "text.secondary",
    fontSize: "12px",
    mt: 0
};
const noFillContainerSxProps = { display: "flex", flexDirection: "row", gap: "8px", alignItems: "center" };
const branColorsContainerSxProps = { display: "flex", flexDirection: "row", mt: "4px", mb: "8px" };
const TransitionComponent = createDraggableTransition({
    draggableProps: {
        handle: ".header"
    }
});
const removeTransperencyFromColorState = (color) => {
    const { r, g, b } = color.rgb;
    return toState(`rgb(${r} ,${g} ,${b} )`);
};
export const ColorPicker = (props) => {
    var _a;
    const { value, title, anchorEl, onColorChange, onClose, predefinedColors, anchorOrigin, transformOrigin, enableTransparency, dataTestId, enableNoFill, disableRestoreFocus, disableAutoFocus, disableEnforceFocus, onTransitionExited } = props;
    const initialColorRef = useRef();
    const [localSelectedColor, setLocalSelectedColor] = useState();
    const [selectedPredefinedId, setSelectedPredefinedId] = useState();
    const [isNoFillSelected, setNoFillSelected] = useState(false);
    const trackingIdPrefix = props.dataTrackingIdPrefix || "color-picker";
    useEffect(() => {
        if (anchorEl) {
            const clorState = toState(value.color);
            initialColorRef.current = {
                color: clorState,
                id: value.id
            };
            setLocalSelectedColor(clorState);
            setSelectedPredefinedId(value.id);
            setNoFillSelected(isSameColor(clorState, TRANSPARENT));
        }
    }, [anchorEl]);
    const handleChangeComplete = (color) => {
        if (props.disabled)
            return;
        onColorChange(color);
    };
    const handleLocalSelectedColorChange = (color) => {
        var _a;
        if (props.disabled)
            return;
        // the first click after "no-fill" state sometimes still include 100% transparency - https://sundaysky.atlassian.net/browse/CB-909
        if (isNoFillSelected && ((_a = color.rgb) === null || _a === void 0 ? void 0 : _a.a) === 0) {
            color.rgb.a = 1;
        }
        if (isNoFillSelected && color.hex === "transparent") {
            color.hex = "#000000";
        }
        setSelectedPredefinedId(null);
        // If transparency is disabled, we need to make sure that the color is not transparent (no fill is transparent).
        // crating new color from color.hex since it does not contain alpha data but can represent fully transparent color.
        // Also, when selecting color after no fill was selected, we need to make sure that the color is not transparent.
        setLocalSelectedColor(isNoFillSelected || !enableTransparency ? removeTransperencyFromColorState(color) : color);
        setNoFillSelected(false);
    };
    const handlePredefinedColorChange = (predefinedColor) => {
        setSelectedPredefinedId(predefinedColor.id);
        setNoFillSelected(false);
        const colorState = toState(predefinedColor.value);
        setLocalSelectedColor(colorState);
        onColorChange(colorState, predefinedColor.id);
    };
    const handleReset = () => {
        setSelectedPredefinedId(null);
        setNoFillSelected(isSameColor(toState(initialColorRef.current.color), TRANSPARENT));
        setLocalSelectedColor(initialColorRef.current.color);
        setSelectedPredefinedId(initialColorRef.current.id);
        onColorChange(initialColorRef.current.color);
    };
    const handleClickNoFill = () => {
        setSelectedPredefinedId(null);
        setNoFillSelected(true);
        setLocalSelectedColor(TRANSPARENT);
        onColorChange(TRANSPARENT);
    };
    return (_jsxs(Popover, { open: !!anchorEl && !!value.color, anchorOrigin: anchorOrigin, transformOrigin: transformOrigin, anchorEl: anchorEl, onClose: onClose, TransitionComponent: TransitionComponent, onTransitionExited: onTransitionExited, disableEnforceFocus: disableEnforceFocus, disableAutoFocus: disableAutoFocus, disableRestoreFocus: disableRestoreFocus, PaperProps: {
            sx: paperSxProps,
            // @ts-ignore
            ["data-testid"]: dataTestId
        }, children: [_jsxs(Box, { className: "header", sx: { cursor: "move", position: "relative" }, children: [_jsx(StyledDraggableIcon, { icon: farEllipsisH }), _jsx(Typography, { variant: "subtitle1", color: "secondary.main", sx: titleSxProps, children: title || "Color Picker" }), _jsx(Tooltip, { arrow: true, title: "Revert", placement: "top", PopperProps: popperModifier, children: _jsx(IconButton, { "data-trackingid": `${trackingIdPrefix}-revert-changes`, color: "primary", sx: resetButtonSxProps, disabled: props.disabled || isSameColor(localSelectedColor, (_a = initialColorRef.current) === null || _a === void 0 ? void 0 : _a.color), onClick: handleReset, children: _jsx(FontAwesomeIcon, { icon: farUndo }) }) })] }), predefinedColors ?
                _jsxs(_Fragment, { children: [_jsx(Typography, { sx: secondarySxProps, children: predefinedColors.title }), _jsx(Box, { sx: branColorsContainerSxProps, children: predefinedColors.colors.map(predefinedColor => (_jsx(ColorButton, { dataTrackingId: `${trackingIdPrefix}-select-brand-color`, dataTestId: `color-picker-color-button-${predefinedColor.name}`, showCheckmark: true, color: predefinedColor.value, tooltip: predefinedColor.name, selected: predefinedColor.id === selectedPredefinedId, disabled: props.disabled, onClick: () => handlePredefinedColorChange(predefinedColor) }, predefinedColor.name))) }), _jsx(Divider, { variant: "fullWidth", sx: { my: "8px" } })] })
                : _jsx(_Fragment, {}), _jsx(CustomColorPicker, { color: localSelectedColor === null || localSelectedColor === void 0 ? void 0 : localSelectedColor.hsl, onChange: (value) => handleLocalSelectedColorChange(value), onChangeComplete: handleChangeComplete, enableTransparency: enableTransparency, disabled: props.disabled }), enableNoFill ?
                _jsxs(_Fragment, { children: [_jsx(Divider, { variant: "fullWidth", sx: { my: "8px" } }), _jsxs(Box, { sx: noFillContainerSxProps, children: [_jsx(ColorButton, { dataTrackingId: `${trackingIdPrefix}-no-fill`, showCheckmark: true, tooltip: "No fill", color: "transparent", selected: isNoFillSelected, disabled: props.disabled, onClick: handleClickNoFill }), _jsx(Typography, { sx: secondarySxProps, children: "No Fill" })] })] })
                : _jsx(_Fragment, {})] }));
};
const paperSxProps = {
    p: "16px",
    pt: 0,
    fontFamily: "Open Sans",
    width: "242px",
    boxSizing: "border-box"
};
const StyledDraggableIcon = styled(FontAwesomeIcon)(({ theme }) => ({
    position: "absolute",
    top: "4px",
    left: "50%",
    transform: "translateX(-50%)",
    color: theme.palette.text.disabled,
    "& svg": {
        width: "16px",
        height: "16px"
    }
}));
const resetButtonSxProps = {
    position: "absolute",
    top: "8px",
    right: 0,
    marginRight: "-12px",
    "& svg": {
        width: "16px",
        height: "16px"
    },
    "&:not([disabled]):not(:hover)": {
        color: "action.active"
    }
};
const pointerSxProps = {
    width: "10px",
    height: "10px",
    border: (theme) => `1px solid ${theme.palette.common.white}`,
    borderRadius: "6px"
};
const eyDropButtonSxProps = {
    mr: "8px",
    "& svg": {
        width: "12px",
        height: "12px"
    }
};
const saturationContainerSxProps = { position: "relative", width: "210px", height: "150px" };
const eyeDropperHueContainerSxProps = { display: "flex", flexDirection: "row", mt: "14px", alignItems: "center" };
const hueContainerSxProps = { position: "relative", height: "12px", flex: 1 };
const alphaContainerSxProps = { position: "relative", height: "12px", mt: "14px" };
const inputsContainerSxProps = (enableTransparency) => ({
    display: "flex",
    flexDirection: "row",
    mt: "10px",
    gap: enableTransparency ? "5px" : "8px",
    "& input": {
        border: "none",
        borderBottom: "2px solid",
        borderColor: "transparent",
        outline: "none",
        fontSize: "14px",
        LineHeight: "20px",
        Letter: "0.15 px",
        padding: "0",
        paddingBottom: "7px",
        textAlign: "center",
        transition: "all 150ms linear"
    },
    "& input:hover": { borderColor: (theme) => theme.palette.primary.light },
    "& input:focus": { borderColor: (theme) => theme.palette.primary.main },
    "& div:first-of-type input": { width: "58px" },
    "& div:not(:first-of-type):not(:last-of-type) input": { width: "25px" },
    "& div:not(:first-of-type):last-of-type input": { width: enableTransparency ? "37px" : "25px" }
});
const popperModifier = {
    modifiers: [
        {
            name: "offset",
            options: {
                offset: [0, -6]
            }
        }
    ]
};
export const CustomColorPicker = CustomPicker((props) => {
    const { rgb, onChange, enableTransparency, hex, hsl } = props;
    const trackingIdPrefix = props.dataTrackingIdPrefix || "color-picker";
    const { open, isSupported } = useEyeDropper();
    const handleRGBInputChange = (partialRgb) => {
        if (props.disabled)
            return;
        let localPartialRgb = partialRgb;
        for (const key in localPartialRgb) {
            if (key === "a") {
                let aFraction = localPartialRgb[key] / 100;
                if (aFraction > 1) {
                    aFraction = 1;
                }
                localPartialRgb[key] = aFraction;
            }
            else if (localPartialRgb[key] > 255) {
                localPartialRgb[key] = 255;
            }
            if (localPartialRgb[key] < 0) {
                localPartialRgb[key] = 0;
            }
        }
        onChange(Object.assign(Object.assign({}, rgb), localPartialRgb));
    };
    return (_jsxs(Box, { children: [_jsx(Box, { sx: saturationContainerSxProps, "data-trackingid": `${trackingIdPrefix}-saturation-value`, children: _jsx(Saturation, Object.assign({}, props, { pointer: () => _jsx(Box, { sx: [pointerSxProps, { transform: "translate(-5px, -5px)" }] }) })) }), _jsxs(Box, { sx: eyeDropperHueContainerSxProps, children: [isSupported()
                        ? (_jsx(Tooltip, { arrow: true, placement: "top", title: "Eye dropper", PopperProps: popperModifier, children: _jsx(IconButton, { "data-trackingid": `${trackingIdPrefix}-eye-dropper`, sx: eyDropButtonSxProps, size: "small", color: "primary", onClick: () => open().then(color => onChange(hexToRGBColor(color.sRGBHex))), children: _jsx(FontAwesomeIcon, { icon: faEyeDropper }) }) }))
                        : _jsx(_Fragment, {}), _jsx(Box, { sx: hueContainerSxProps, "data-trackingid": `${trackingIdPrefix}-hue-slider`, children: _jsx(Hue, Object.assign({}, props, { pointer: () => _jsx(Box, { sx: [pointerSxProps, { transform: "translateX(-5px)" }] }) })) })] }), enableTransparency ?
                _jsx(Box, { sx: alphaContainerSxProps, "data-trackingid": `${trackingIdPrefix}-alpha-slider`, children: _jsx(Alpha, Object.assign({}, props, { hsl: hsl, pointer: () => _jsx(Box, { sx: [pointerSxProps, { transform: "translateX(-5px)" }] }) })) })
                : _jsx(_Fragment, {}), _jsxs(Box, { sx: inputsContainerSxProps(enableTransparency), children: [_jsx(Box, { sx: { flex: 1 }, "data-testid": "color-picker-hex-value-container", children: _jsx(Tooltip, { arrow: true, title: "HEX", placement: "top", PopperProps: popperModifier, children: _jsx(Box, { sx: { width: "fit-content" }, "data-trackingid": `${trackingIdPrefix}-hex-value`, children: _jsx(EditableInput, { value: (hex === "transparent"
                                        ? "-"
                                        : hex.replace("#", "")), onChange: (value) => onChange(toState(value)) }) }) }) }), ["r", "g", "b"].map(key => _jsx(Tooltip, { arrow: true, placement: "top", title: key.toUpperCase(), PopperProps: popperModifier, children: _jsx(Box, { "data-trackingid": `${trackingIdPrefix}-rgb-value`, children: _jsx(EditableInput, { value: (hex === "transparent"
                                    ? "-"
                                    : rgb[key]), onChange: (value) => handleRGBInputChange({ [key]: Number(value) }) }) }) }, key)), enableTransparency ?
                        _jsx(Tooltip, { arrow: true, title: "Alpha", placement: "top", PopperProps: popperModifier, children: _jsx(Box, { sx: { width: "fit-content" }, "data-trackingid": `${trackingIdPrefix}-alpha-value`, children: _jsx(EditableInput, { value: (hex === "transparent"
                                        ? "-"
                                        : `${Math.round(rgb.a * 100)}%`), onChange: (value) => handleRGBInputChange({ a: Number(value.toString().replace("%", "")) }) }) }) })
                        : _jsx(_Fragment, {})] })] }));
});
//# sourceMappingURL=ColorPicker.js.map